// src/store/authSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { authAPI } from '../api/auth.js';

// Асинхронные действия
export const login = createAsyncThunk(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await authAPI.login(credentials);
      const { user, access_token, refresh_token } = response.data.data;
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      return { user, access_token, refresh_token };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Login failed');
    }
  }
);

// ============================================================
// 🔴 РЕГИСТРАЦИЯ ТЕПЕРЬ ВХОДИТ СРАЗУ (2026-10-29).
//
//    Было: `return response.data.data` — токены в localStorage НЕ
//    клались, `isAuthenticated` не ставился. После регистрации человек
//    оставался гостем: следующий же запрос (корзина, профиль) уходил
//    без `Authorization`.
//
//    Казалось, что всё работает: ни ошибки, ни падения — просто «тихий
//    гость». Тот же класс, что дефект фото: неверное поле не даёт 500,
//    оно даёт пустоту.
//
//    Бэкенд с этой же правки возвращает `access_token`/`refresh_token`
//    в ответе `POST /auth/register` (разбор — в `authController.js`).
// ============================================================
export const register = createAsyncThunk(
  'auth/register',
  async (data, { rejectWithValue }) => {
    try {
      const response = await authAPI.register(data);
      const { user, access_token, refresh_token } = response.data.data || {};

      // ⚠️ Токены кладём ТОЛЬКО если оба пришли. Иначе лучше явно
      //    остаться гостем, чем записать `undefined` в localStorage:
      //    `client.js` отправил бы `Bearer undefined` — то есть 401
      //    с непонятной причиной вместо честного «не вошёл».
      if (access_token && refresh_token) {
        localStorage.setItem('access_token', access_token);
        localStorage.setItem('refresh_token', refresh_token);
        return { user, access_token, refresh_token };
      }
      return { user: user || null, access_token: null, refresh_token: null };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Registration failed');
    }
  }
);

export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      await authAPI.logout();
    } catch (error) {
      // Даже если API вернет ошибку, мы все равно очищаем локальное состояние
    }
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
  }
);

export const getProfile = createAsyncThunk(
  'auth/getProfile',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authAPI.getProfile();
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to load profile');
    }
  }
);

export const updateProfile = createAsyncThunk(
  'auth/updateProfile',
  async (data, { rejectWithValue }) => {
    try {
      const response = await authAPI.updateProfile(data);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update profile');
    }
  }
);

// Начальное состояние
const initialState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
};

// Slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setUser: (state, action) => {
      state.user = action.payload;
      state.isAuthenticated = !!action.payload;
    },
  },
  extraReducers: (builder) => {
    // Login
    builder
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.isAuthenticated = true;
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Register
    builder
      .addCase(register.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.isLoading = false;
        // 🔑 СТАВИМ ВОШЁДШЕЕ СОСТОЯНИЕ — иначе регистрация «проходит»,
        //    а сайт по-прежнему считает человека гостем.
        //    `isAuthenticated` поднимаем ТОЛЬКО при живом токене: если
        //    сервер токен не прислал, честно остаёмся гостем, а не
        //    рисуем «вошёл» без права читать профиль.
        if (action.payload?.access_token) {
          state.user = action.payload.user || null;
          state.isAuthenticated = true;
        }
      })
      .addCase(register.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Logout
    builder
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.isAuthenticated = false;
        state.error = null;
      });

    // Get Profile
    builder
      .addCase(getProfile.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
      })
      .addCase(getProfile.rejected, (state) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
      });

    // Update Profile
    builder
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.user = action.payload;
      });
  },
});

export const { clearError, setUser } = authSlice.actions;
export default authSlice.reducer;